import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { ChevronRightIcon, ExternalLinkIcon, ReloadIcon } from '@radix-ui/react-icons';

import { ErrorPage } from '../../../ErrorPage';
import { SocialSecurityLoading } from '../Loading';
import { useThemeContext } from '../../../../contexts/ThemeContext';
import { getOrCreateBrowserKey } from '../../../../utils/fingerprint_utils';
import { resolveColor } from '../../theme/utils/resolveColor';
import { PoweredByQITech } from '../components/PoweredByQITech';
import * as S from './styles';

interface FolderMode {
    confiaSignUrl?: string;
    pollAndAdvance: () => Promise<number | undefined>;
}

interface ConfiaPageProps {
    signData: any;
    setSignerStep: (step: string) => void;
    setSignData: (data: any) => void;
    folderMode?: FolderMode;
}

export const SocialSecurityConfiaPage = ({
    signData,
    setSignerStep,
    setSignData,
    folderMode,
}: ConfiaPageProps) => {
    const { clientThemeSettings } = useThemeContext();
    const primary = resolveColor('primary', clientThemeSettings);

    const isFolderMode = !!folderMode;
    const initialError = { has_error: false, status: 0 };
    const [loadingStatus, setLoadingStatus] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<{ has_error: boolean; status: number }>(initialError);

    const handleNextStep = useCallback(async () => {
        let browserKey: string | null = null;
        try {
            browserKey = await getOrCreateBrowserKey();
        } catch (_) {}

        const payload = {
            step: 'face_recognition',
            event_date: new Date().toISOString(),
            image_key: 'generated_by_confia',
            device_data: { device_key: browserKey },
        };

        const config = { headers: { Authorization: signData.signer_token } };

        setLoading(true);

        axios
            .patch('/signer', payload, config)
            .then((response) => {
                setSignerStep(response.data.next_step);
                setSignData({ ...signData, ...response.data });
            })
            .catch((error) => {
                const status = [401, 403, 409].includes(error.response?.status)
                    ? error.response.status
                    : 500;
                setError({ has_error: true, status });
            })
            .finally(() => {
                setLoading(false);
            });
    }, [signData, setSignData, setSignerStep]);

    const verifyDocumentStatus = useCallback(async () => {
        const config = { headers: { Authorization: signData.signer_token } };

        axios.get('/signer/confia_status_url', config).then((response) => {
            if (response.data.status === 'signed') handleNextStep();

            if (['blocked', 'deleted', 'refused'].includes(response.data.status))
                setSignerStep('deactivated');
        });
    }, [signData.signer_token, handleNextStep, setSignerStep]);

    useEffect(() => {
        if (isFolderMode) {
            let active = true;
            const tick = () => {
                folderMode!.pollAndAdvance().then((status) => {
                    if (active && status) {
                        setError({ has_error: true, status });
                    }
                });
            };
            tick();
            const interval = setInterval(tick, 3000);
            return () => {
                active = false;
                clearInterval(interval);
            };
        }

        verifyDocumentStatus();

        const interval = setInterval(verifyDocumentStatus, 3000);

        return () => clearInterval(interval);
    }, [isFolderMode, folderMode, verifyDocumentStatus]);

    const handleStartSignature = () => {
        setLoadingStatus(true);

        const currentUrl = window.location.href;
        const confiaUrl = isFolderMode ? folderMode!.confiaSignUrl : signData?.confia_sign_url;

        const finalUrl = `${confiaUrl}&urlCallback=${encodeURIComponent(currentUrl)}`;

        window.open(finalUrl, '_blank');

        setTimeout(() => setLoadingStatus(false), 2000);
    };

    if (loading) {
        return <SocialSecurityLoading />;
    }

    return (
        <S.PageWrapper>
            <S.LogoSection>
                {clientThemeSettings?.logo_url && (
                    <S.LogoImage src={clientThemeSettings.logo_url} alt="logo" />
                )}
            </S.LogoSection>

            <S.ScrollContent>
                <S.HeaderBlock>
                    <S.Title $color={primary}>Dupla jornada em site de parceiro</S.Title>
                    <S.Subtitle>
                        Para continuar sua assinatura, complete a jornada no nosso provedor
                        parceiro <strong>Confia</strong>.
                    </S.Subtitle>
                </S.HeaderBlock>

                <S.StepList>
                    <S.StepCard $color={primary}>
                        <S.StepBadge $color={primary}>
                            <ExternalLinkIcon width={18} height={18} />
                        </S.StepBadge>
                        <S.StepBody>
                            <S.StepTitle $color={primary}>Redirecionamento para a Confia</S.StepTitle>
                            <S.StepText>
                                Ao clicar no botão você será{' '}
                                <strong>direcionado para o site da Confia</strong> e deve finalizar
                                a sua assinatura no site deles.
                            </S.StepText>
                        </S.StepBody>
                    </S.StepCard>

                    <S.StepCard $color={primary}>
                        <S.StepBadge $color={primary}>
                            <ReloadIcon width={18} height={18} />
                        </S.StepBadge>
                        <S.StepBody>
                            <S.StepTitle $color={primary}>Retorno para o QI Sign</S.StepTitle>
                            <S.StepText>
                                Depois de finalizar a jornada na Confia, você será redirecionado{' '}
                                <strong>de volta para o QI Sign automaticamente</strong>. Basta
                                aguardar alguns segundos para a tela ser atualizada.
                            </S.StepText>
                        </S.StepBody>
                    </S.StepCard>
                </S.StepList>
            </S.ScrollContent>

            {error.has_error && (
                <ErrorPage
                    error={error}
                    redirectToLogin={() => setSignerStep('login')}
                    resetError={() => setError(initialError)}
                    clientThemeSettings={clientThemeSettings}
                />
            )}

            <S.Footer>
                <S.SubmitButton
                    $backgroundColor={primary}
                    $disabled={loadingStatus}
                    onClick={loadingStatus ? undefined : handleStartSignature}
                >
                    {loadingStatus ? (
                        'Carregando...'
                    ) : (
                        <>
                            Realizar validação biométrica
                            <ChevronRightIcon width={24} height={24} />
                        </>
                    )}
                </S.SubmitButton>
                <PoweredByQITech />
            </S.Footer>
        </S.PageWrapper>
    );
};
